export const switcher = () => {
  const expandedItemClasses = ["bg-white", "drop-shadow-lg"],
    collapsedItemClasses = ["collapsed", "cursor-pointer", "hover:bg-white/30"],
    collapsedHeight = 55; // Collapsed height in pixels

  /** Ensures dividers around the currently expanded item are hidden */
  const setDividerVisibility = (item: HTMLElement) => {
    // Get dividers that should be hidden
    const itemNumber = parseInt(item.dataset.item as string);

    const currentSwitcherRoot = item.closest("[data-require='switcher']") as HTMLElement;

    const allDividers = currentSwitcherRoot.querySelectorAll("hr");

    // Make all dividers visible...
    allDividers.forEach(divider => divider.classList.remove("opacity-0"));

    // ...except around the currently active element
    const dividersAroundActiveElement: Element[] = [];

    // Start with the divider immediately *after* the currently active item
    currentSwitcherRoot
      .querySelectorAll(`[data-item='${itemNumber}'] + hr`)
      .forEach(divider => dividersAroundActiveElement.push(divider));

    // Also hide the preceding divider, i.e. the one *after* the previous item (or simply the first divider if the active item is the first one)
    if (itemNumber === 1) {
      dividersAroundActiveElement.push(
        currentSwitcherRoot.querySelector("hr.first") as Element
      );
    } else {
      dividersAroundActiveElement.push(
        currentSwitcherRoot.querySelector(
          `[data-item='${itemNumber - 1}'] + hr`
        ) as Element
      );
    }

    dividersAroundActiveElement.forEach(divider => divider.classList.add("opacity-0"));
  };

  const collapse = (element: HTMLElement) => {
    if (!element.classList.contains(expandedItemClasses[0])) {
      // Element already collapsed
      return;
    }

    expandedItemClasses.forEach(className => {
      element.classList.remove(className);
    });
    collapsedItemClasses.forEach(className => {
      element.classList.add(className);
    });

    element.querySelector("[data-item-wrapper]")?.classList.remove("py-40");

    const originalHeight = element.scrollHeight;

    // Set original height to allow item height to animate to its collapsed height
    element.style.height = `${originalHeight}px`;

    element.style.height = `${collapsedHeight}px`;
  };

  const expand = (element: HTMLElement) => {
    expandedItemClasses.forEach(className => {
      element.classList.add(className);
    });

    collapsedItemClasses.forEach(className => {
      element.classList.remove(className);
    });

    element.querySelector("[data-item-wrapper]")?.classList.add("py-40");

    const currentSwitcherRoot = element.closest("[data-switcher-items]");

    if (!currentSwitcherRoot) {
      throw "Unable to resolve current Switcher block root element.";
    }

    const targetHeight = element.scrollHeight;

    element.style.height = `${targetHeight}px`;

    setDividerVisibility(element);

    const shouldScrollToElement = (el: HTMLElement): boolean => {
      const rect = el.getBoundingClientRect();

      return rect.top < 0; // Top of element is partly outside the viewport
    };

    const scrollIntoViewIfNeeded = (el: HTMLElement) => {
      if (shouldScrollToElement(el)) {
        el.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
      }
    };

    // Remove explicit height to allow element to resize when browser size changes
    element.style.height = "";

    // Scroll, if needed, to make expanded element visible in viewport
    scrollIntoViewIfNeeded(element);

    const invisibleImageClasses = ["opacity-0"],
      visibleImageClasses: string[] = [];

    // Hide all desktop images except the expanded item's
    if (currentSwitcherRoot) {
      currentSwitcherRoot.querySelectorAll<HTMLElement>("img[data-item]").forEach(img => {
        if (img.dataset.item === element.dataset.item) {
          // Make image visible
          invisibleImageClasses.forEach(className => img.classList.remove(className));
          visibleImageClasses.forEach(className => img.classList.add(className));
        } else {
          // Hide image
          visibleImageClasses.forEach(className => img.classList.remove(className));
          invisibleImageClasses.forEach(className => img.classList.add(className));
        }
      });
    }
  };

  const switcherRootElements = document.querySelectorAll(
    "section[data-require='switcher']"
  );

  switcherRootElements.forEach(switcherRoot => {
    const items = switcherRoot.querySelectorAll<HTMLElement>(
      "[data-switcher-block] div[data-item]"
    );

    items.forEach(elm => {
      elm.addEventListener("click", evt => {
        const element = evt.target as HTMLElement | null;

        const wrapper = element?.closest("div[data-item]") as HTMLElement | null;

        if (!wrapper) {
          return;
        }

        // Collapse all items except the current one

        const itemNumber = wrapper.dataset.item;

        items.forEach(itemWrapper => {
          if (itemWrapper.dataset.item === itemNumber) {
            expand(itemWrapper);
          } else {
            collapse(itemWrapper);
          }
        });
      });
    });
  });
};
